// Invoices — GST tax-invoice management.
//
// List reads the paginated useInvoices({ limit, offset, search, status }) hook;
// create/update/delete go through the invoice mutations which invalidate the
// ["invoices"] key. Totals shown in the form are a live client-side preview —
// the server recomputes them authoritatively on save.
import { useState, useContext } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  TextField,
  Stack,
  Button,
  IconButton,
  Tooltip,
  Checkbox,
  Chip,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutlined";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import SendOutlinedIcon from "@mui/icons-material/SendOutlined";
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined";
import PendingActionsOutlinedIcon from "@mui/icons-material/PendingActionsOutlined";
import DomainOutlinedIcon from "@mui/icons-material/DomainOutlined";
import DnsOutlinedIcon from "@mui/icons-material/DnsOutlined";
import DashboardLayout from "../layouts/DashboardLayout";
import { COLORS } from "../theme/tokens";
import FlatCard from "../components/FlatCard";
import { HEADING_FONT } from "../theme/fonts";
import PaginationFooter from "../components/PaginationFooter";
import TableSkeleton from "../components/TableSkeleton";
import SelectionActionBar from "../components/SelectionActionBar";
import AddInvoiceModal from "../components/AddInvoiceModal";
import { useTableSelection } from "../hooks/useTableSelection";
import api from "../api";
import {
  useInvoices,
  useDeleteInvoice,
  useBulkDeleteInvoices,
  useDomains,
  useServers,
} from "../hooks/queries";
import { useToast } from "../components/ToastProvider";
import { useConfirm } from "../components/ConfirmProvider";
import { AuthContext } from "../context/AuthContext";

const INVOICE_GRADIENT = "linear-gradient(135deg, #3B82F6 0%, #1D4ED8 100%)";

// Status chip colours.
const STATUS_STYLES = {
  Draft: { color: "#94a3b8", bg: "rgba(148,163,184,0.14)" },
  Sent: { color: "#38bdf8", bg: "rgba(56,189,248,0.14)" },
  Paid: { color: "#34d399", bg: "rgba(52,211,153,0.14)" },
  Overdue: { color: "#f87171", bg: "rgba(248,113,113,0.14)" },
  Cancelled: { color: "#9ca3af", bg: "rgba(156,163,175,0.14)" },
};

// Filter tabs → { key for the API, label, counts key }.
const FILTER_TABS = [
  { key: "all", label: "All" },
  { key: "draft", label: "Draft" },
  { key: "sent", label: "Sent" },
  { key: "paid", label: "Paid" },
  { key: "overdue", label: "Overdue" },
  { key: "cancelled", label: "Cancelled" },
];

const inr = (n) =>
  (Number(n) || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

function Invoices() {
  // Flat spec design tokens (light-only). Replaces the old glass theme
  // (useThemeColors) so the page is locked to the "Blue · Light" system.
  const colors = {
    text: COLORS.ink,
    textMuted: COLORS.muted,
    textFaint: COLORS.faint,
    cardBg: COLORS.surface,
    cardBorder: COLORS.hairline,
    cardBorderHover: COLORS.borderHover,
    hover: COLORS.tile,
    divider: COLORS.hairline,
  };
  const toast = useToast();
  const confirm = useConfirm();
  const navigate = useNavigate();

  const { hasPermission } = useContext(AuthContext);
  const canAdd = hasPermission("invoices", "add");
  const canEdit = hasPermission("invoices", "edit");
  const canDelete = hasPermission("invoices", "delete");

  // --- List state ---
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");

  const params = {
    limit: pageSize,
    offset: (page - 1) * pageSize,
    search: search.trim(),
    status,
  };
  const { data, isLoading } = useInvoices(params, { keepPreviousData: true });
  const invoices = data?.rows || [];
  const total = data?.total || 0;
  const counts = data?.counts || {};

  // --- Billing dashboard (spec §4.8): KPIs ---
  // Full invoice / domain / server sets (cached, shared with other pages) for
  // the figures — independent of the paginated table below.
  const { data: allInvoicesData } = useInvoices();
  const allInvoices = Array.isArray(allInvoicesData)
    ? allInvoicesData
    : allInvoicesData?.rows || [];
  const { data: domainsData } = useDomains();
  const domainsArr = Array.isArray(domainsData)
    ? domainsData
    : domainsData?.rows || [];
  const { data: serversData } = useServers();
  const serversArr = Array.isArray(serversData)
    ? serversData
    : serversData?.rows || [];

  // Spend this month = sum of non-cancelled invoice totals dated this month.
  const nowDate = new Date();
  const spendThisMonth = allInvoices.reduce((sum, inv) => {
    if (inv.status === "Cancelled") return sum;
    const d = inv.invoiceDate ? new Date(inv.invoiceDate) : null;
    if (!d || Number.isNaN(d.getTime())) return sum;
    return d.getMonth() === nowDate.getMonth() &&
      d.getFullYear() === nowDate.getFullYear()
      ? sum + (Number(inv.total) || 0)
      : sum;
  }, 0);

  // Outstanding = money still owed: totals of invoices that are Sent or Overdue
  // (i.e. billed but not yet Paid / Cancelled / still Draft).
  let outstandingCount = 0;
  const outstanding = allInvoices.reduce((sum, inv) => {
    if (inv.status === "Sent" || inv.status === "Overdue") {
      outstandingCount += 1;
      return sum + (Number(inv.total) || 0);
    }
    return sum;
  }, 0);

  // Each card is clickable: the two money cards filter the table below, the two
  // inventory cards deep-link to their own page (same pattern as Dashboard).
  const billingStats = [
    {
      label: "Spend this month",
      value: `₹${inr(spendThisMonth)}`,
      sub: nowDate.toLocaleString("en-IN", { month: "long", year: "numeric" }),
      icon: <PaymentsOutlinedIcon />,
      accent: COLORS.primary,
      onClick: () => applyStatus("all"),
    },
    {
      label: "Outstanding",
      value: `₹${inr(outstanding)}`,
      sub: `${outstandingCount} unpaid invoice${outstandingCount === 1 ? "" : "s"}`,
      icon: <PendingActionsOutlinedIcon />,
      accent: "#D97706",
      onClick: () => applyStatus("overdue"),
    },
    {
      label: "Domains",
      value: domainsArr.length,
      sub: "in portfolio",
      icon: <DomainOutlinedIcon />,
      accent: "#16A34A",
      onClick: () => navigate("/domains"),
    },
    {
      label: "Servers",
      value: serversArr.length,
      sub: "in inventory",
      icon: <DnsOutlinedIcon />,
      accent: COLORS.primaryDark,
      onClick: () => navigate("/servers"),
    },
  ];

  function applyStatus(next) {
    setStatus(next);
    setPage(1);
  }

  // --- Mutations ---
  const deleteInvoice = useDeleteInvoice();
  const bulkDelete = useBulkDeleteInvoices();

  // --- Selection ---
  const pageIds = invoices.map((i) => i.id);
  const {
    selectedIds,
    toggleRow,
    toggleAll,
    clear: clearSelection,
    allSelected,
    someSelected,
  } = useTableSelection(pageIds);
  const [bulkBusy, setBulkBusy] = useState(false);

  // Row-level busy state for PDF download / email send.
  const [busyRow, setBusyRow] = useState(null);

  // --- Add / Edit Invoice modal (spec §5) ---
  const [addOpen, setAddOpen] = useState(false);
  const [editInvoiceId, setEditInvoiceId] = useState(null);
  const invoiceModalOpen = addOpen || editInvoiceId !== null;

  function openCreate() {
    setAddOpen(true);
  }

  function openEdit(id) {
    setEditInvoiceId(id);
  }

  function closeInvoiceModal() {
    setAddOpen(false);
    setEditInvoiceId(null);
  }

  function handleInvoiceSaved({ isEdit } = {}) {
    // The create/update mutations already invalidate the ["invoices"] cache.
    closeInvoiceModal();
    toast.success(isEdit ? "Invoice updated successfully" : "Invoice added successfully");
  }

  // --- Delete ---
  async function handleDelete(id, invoiceNo) {
    const ok = await confirm({
      title: "Delete invoice?",
      message: `Invoice ${invoiceNo} will be permanently removed.`,
      danger: true,
      confirmLabel: "Delete",
    });
    if (!ok) return;
    try {
      await deleteInvoice.mutateAsync(id);
      toast.success("Invoice deleted");
    } catch (err) {
      toast.error(err.message || "Failed to delete invoice");
    }
  }

  async function handleDeleteSelected() {
    const ids = [...selectedIds];
    if (!ids.length) return;
    const ok = await confirm({
      title: `Delete ${ids.length} invoice${ids.length === 1 ? "" : "s"}?`,
      message: "The selected invoices will be permanently removed.",
      danger: true,
      confirmLabel: "Delete",
    });
    if (!ok) return;
    setBulkBusy(true);
    try {
      const res = await bulkDelete.mutateAsync(ids);
      clearSelection();
      toast.success(`Deleted ${res?.deleted ?? ids.length} invoice(s)`);
    } catch (err) {
      toast.error(err.message || "Failed to delete selected invoices");
    } finally {
      setBulkBusy(false);
    }
  }

  // --- PDF download (blob fetch with auth token; api.js only handles JSON) ---
  async function handleDownloadPdf(inv) {
    setBusyRow(inv.id);
    try {
      const token = localStorage.getItem("authToken") || "";
      const res = await fetch(`${api.baseUrl}/api/invoices/${inv.id}/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Failed to generate PDF");
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Invoice-${String(inv.invoiceNo).replace(/[^\w.-]+/g, "_")}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(err.message || "Failed to download PDF");
    } finally {
      setBusyRow(null);
    }
  }

  // --- View PDF (open in a new browser tab instead of downloading) ---
  // Same auth-token blob fetch as the download, but we open the object URL in a
  // new tab so the user can preview the invoice first. The URL is revoked after
  // a delay so the new tab has time to load it.
  async function handleViewPdf(inv) {
    setBusyRow(inv.id);
    try {
      const token = localStorage.getItem("authToken") || "";
      const res = await fetch(`${api.baseUrl}/api/invoices/${inv.id}/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Failed to generate PDF");
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      window.open(url, "_blank", "noopener,noreferrer");
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch (err) {
      toast.error(err.message || "Failed to open PDF");
    } finally {
      setBusyRow(null);
    }
  }

  // --- Email to client ---
  async function handleSendEmail(inv) {
    if (!inv.clientEmail) {
      toast.error("This invoice has no client email");
      return;
    }
    const ok = await confirm({
      title: "Email this invoice?",
      message: `The PDF will be emailed to ${inv.clientEmail}.`,
      confirmLabel: "Send",
    });
    if (!ok) return;
    setBusyRow(inv.id);
    try {
      const res = await api.post(`/invoices/${inv.id}/send`);
      toast.success(`Invoice emailed to ${res?.sentTo || inv.clientEmail}`);
    } catch (err) {
      toast.error(err.message || "Failed to send email");
    } finally {
      setBusyRow(null);
    }
  }

  const fieldSx = {
    "& .MuiOutlinedInput-root": {
      background: colors.cardBg,
      color: colors.text,
      borderRadius: 2,
      "& fieldset": { borderColor: colors.cardBorder },
      "&:hover fieldset": { borderColor: colors.cardBorderHover },
      "&.Mui-focused fieldset": { borderColor: "#3B82F6" },
    },
    "& input, & textarea": { color: colors.text },
    "& .MuiSelect-select": { color: colors.text },
  };

  // 44 checkbox | # | invoice no | client | date | total | status | actions
  const GRID_COLUMNS = "44px 56px 1.3fr 1.4fr 110px 120px 110px 200px";

  // Per-column text alignment — shared by the header and the row cells so a
  // header always sits exactly above its values.
  const COLUMN_ALIGN = {
    "#": "center",
    "Invoice No": "left",
    Client: "left",
    Date: "left",
    Total: "center",
    Status: "center",
    Actions: "center",
  };

  return (
    <DashboardLayout>
      {/* Header */}
      <Stack
        direction={{ xs: "column", sm: "row" }}
        alignItems={{ xs: "stretch", sm: "center" }}
        spacing={2}
        sx={{ mb: 3, justifyContent: "space-between" }}
      >
        <Stack direction="row" spacing={2} alignItems="center" sx={{ minWidth: 0 }}>
          <Box sx={{ minWidth: 0 }}>
            <Typography
              variant="h4"
              sx={{ fontFamily: HEADING_FONT, fontWeight: 700, color: colors.text, letterSpacing: "-0.5px" }}
            >
              Invoices
            </Typography>
            <Typography variant="body2" sx={{ color: colors.textMuted, mt: 0.5 }}>
              {total} invoice{total === 1 ? "" : "s"} total
            </Typography>
          </Box>
        </Stack>
        {canAdd && (
          <Button
            onClick={openCreate}
            startIcon={<AddIcon />}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              fontSize: 14,
              px: 2.5,
              py: 1.1,
              borderRadius: 2,
              color: "#fff",
              background: "linear-gradient(135deg, #3B82F6 0%, #1D4ED8 100%)",
              boxShadow: "0 4px 14px rgba(59, 130, 246, 0.35)",
              alignSelf: { xs: "flex-end", sm: "center" },
              "&:hover": { boxShadow: "0 8px 22px rgba(59, 130, 246, 0.45)", transform: "translateY(-1px)" },
              transition: "all 0.15s ease",
            }}
          >
            New Invoice
          </Button>
        )}
      </Stack>

      {/* Billing KPI cards (spec §4.8) */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            sm: "repeat(2, 1fr)",
            lg: "repeat(4, 1fr)",
          },
          gap: 2.5,
          mb: 2.5,
        }}
      >
        {billingStats.map((s) => (
          <FlatCard
            key={s.label}
            hover
            accent={s.accent}
            role="button"
            tabIndex={0}
            onClick={s.onClick}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                s.onClick();
              }
            }}
            sx={{ p: 2.5 }}
          >
            <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1.25 }}>
              <Box
                sx={{
                  width: 44,
                  height: 44,
                  flexShrink: 0,
                  borderRadius: "12px",
                  background: `${s.accent}14`,
                  color: s.accent,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  "& .MuiSvgIcon-root": { fontSize: 24 },
                }}
              >
                {s.icon}
              </Box>
              <Typography
                sx={{
                  fontFamily: HEADING_FONT,
                  fontSize: 24,
                  fontWeight: 800,
                  color: colors.text,
                  letterSpacing: "-0.5px",
                  lineHeight: 1,
                  display: "flex",
                  alignItems: "center",
                }}
              >
                {s.value}
              </Typography>
            </Stack>
            <Typography
              sx={{
                fontSize: 12,
                fontWeight: 600,
                color: colors.textMuted,
                textTransform: "uppercase",
                letterSpacing: "0.5px",
                mt: 0.75,
              }}
            >
              {s.label}
            </Typography>
            <Typography sx={{ fontSize: 12, color: colors.textFaint, mt: 0.25 }}>
              {s.sub}
            </Typography>
          </FlatCard>
        ))}
      </Box>

      {/* Search + filter tabs */}
      <Stack direction={{ xs: "column", md: "row" }} spacing={2} sx={{ mb: 2 }} alignItems={{ md: "center" }}>
        <TextField
          placeholder="Search invoice no, client, GSTIN…"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
          }}
          sx={{
            ...fieldSx,
            flexGrow: 1,
            width: { xs: "100%", md: "auto" },
            minWidth: { md: 300 },
            "& .MuiOutlinedInput-input": {
              paddingTop: "8px",
              paddingBottom: "8px",
            },
          }}
        />
        <Stack
          direction="row"
          spacing={1}
          sx={{
            flexWrap: "wrap",
            gap: 1,
            flexShrink: 0,
            ml: { md: "auto" },
            alignItems: "center",
            justifyContent: { xs: "flex-start", md: "flex-end" },
          }}
        >
          {FILTER_TABS.map((tab) => {
            const active = status === tab.key;
            const c = counts[tab.key];
            return (
              <Chip
                key={tab.key}
                label={`${tab.label}${c !== undefined ? ` (${c})` : ""}`}
                onClick={() => {
                  setStatus(tab.key);
                  setPage(1);
                }}
                sx={{
                  fontWeight: 600,
                  fontSize: 12.5,
                  color: active ? "#fff" : colors.textMuted,
                  background: active ? INVOICE_GRADIENT : colors.cardBg,
                  border: `1px solid ${active ? "transparent" : colors.cardBorder}`,
                  "&:hover": { background: active ? INVOICE_GRADIENT : colors.hover },
                }}
              />
            );
          })}
        </Stack>
      </Stack>

      <SelectionActionBar
        count={selectedIds.size}
        onDelete={canDelete ? handleDeleteSelected : undefined}
        onClear={clearSelection}
        busy={bulkBusy}
        colors={colors}
      />

      {/* Table */}
      <FlatCard sx={{ overflow: "hidden" }}>
        {/* Horizontal scroll wrapper — full table on mobile, swipe sideways */}
        <Box sx={{ overflowX: "auto" }}>
        {/* Header row */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: GRID_COLUMNS,
            minWidth: { xs: "max-content", md: "auto" },
            gap: 2,
            px: 3,
            py: 1.75,
            alignItems: "center",
            borderBottom: `1px solid ${colors.divider}`,
            background: colors.hover,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Checkbox
              size="small"
              checked={allSelected}
              indeterminate={someSelected}
              onChange={toggleAll}
              sx={{ p: 0, color: colors.textMuted }}
            />
          </Box>
          {["#", "Invoice No", "Client", "Date", "Total", "Status", "Actions"].map((h) => (
            <Typography
              key={h}
              sx={{
                fontFamily: HEADING_FONT,
                fontSize: 11,
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: "0.5px",
                color: colors.textMuted,
                textAlign: h === "Client" ? "center" : COLUMN_ALIGN[h] || "left",
                ml: h === "Client" ? "-32px" : 0,
              }}
            >
              {h}
            </Typography>
          ))}
        </Box>

        {/* Rows */}
        {isLoading ? (
          <TableSkeleton rows={pageSize} columns={7} showAvatar={false} />
        ) : invoices.length === 0 ? (
          <Box sx={{ py: 8, px: 3, textAlign: "center" }}>
            <ReceiptLongOutlinedIcon sx={{ fontSize: 44, color: colors.textFaint, mb: 1 }} />
            <Typography sx={{ fontFamily: HEADING_FONT, fontSize: 15, fontWeight: 600, color: colors.text }}>
              No invoices found
            </Typography>
            <Typography sx={{ fontSize: 13, color: colors.textMuted, mt: 0.5 }}>
              {canAdd ? 'Click "New Invoice" to create one' : "No invoices to show"}
            </Typography>
          </Box>
        ) : (
          invoices.map((inv, idx) => {
            const st = STATUS_STYLES[inv.status] || STATUS_STYLES.Draft;
            return (
              <Box
                key={inv.id}
                sx={{
                  display: "grid",
                  gridTemplateColumns: GRID_COLUMNS,
                  minWidth: { xs: "max-content", md: "auto" },
                  gap: 2,
                  px: 3,
                  py: 1.85,
                  alignItems: "center",
                  borderBottom: idx < invoices.length - 1 ? `1px solid ${colors.divider}` : "none",
                  "&:hover": { background: colors.hover },
                  transition: "background 0.18s ease",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Checkbox
                    size="small"
                    checked={selectedIds.has(inv.id)}
                    onChange={() => toggleRow(inv.id)}
                    sx={{ p: 0, color: colors.textMuted }}
                  />
                </Box>
                <Typography sx={{ fontFamily: HEADING_FONT, fontSize: 13, color: colors.textMuted, textAlign: "center" }}>
                  {(page - 1) * pageSize + idx + 1}
                </Typography>
                <Typography sx={{ fontFamily: HEADING_FONT, fontSize: 13, fontWeight: 700, color: colors.text }}>
                  {inv.invoiceNo}
                </Typography>
                <Box sx={{ minWidth: 0, ml: -1 }}>
                  <Typography noWrap sx={{ fontSize: 13.5, fontWeight: 600, color: colors.text }}>
                    {inv.clientName}
                  </Typography>
                  {inv.clientEmail && (
                    <Typography noWrap sx={{ fontSize: 12, color: colors.textMuted }}>
                      {inv.clientEmail}
                    </Typography>
                  )}
                </Box>
                <Typography sx={{ fontSize: 13, color: colors.textMuted }}>
                  {inv.invoiceDate ? String(inv.invoiceDate).slice(0, 10) : "—"}
                </Typography>
                <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: "#3B82F6", textAlign: "center", fontVariantNumeric: "tabular-nums" }}>
                  ₹{inr(inv.total)}
                </Typography>
                <Box sx={{ display: "flex", justifyContent: "center" }}>
                  <Chip
                    label={inv.status}
                    size="small"
                    sx={{ fontWeight: 700, fontSize: 11.5, color: st.color, background: st.bg, height: 24 }}
                  />
                </Box>
                <Stack direction="row" spacing={0.25} justifyContent="center" sx={{ ml: 2 }}>
                  <Tooltip title="View PDF">
                    <IconButton
                      size="small"
                      disabled={busyRow === inv.id}
                      onClick={() => handleViewPdf(inv)}
                      sx={{ color: colors.textMuted, "&:hover": { background: colors.hover, color: "#3B82F6" } }}
                    >
                      <VisibilityOutlinedIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Download PDF">
                    <IconButton
                      size="small"
                      disabled={busyRow === inv.id}
                      onClick={() => handleDownloadPdf(inv)}
                      sx={{ color: colors.textMuted, "&:hover": { background: colors.hover, color: "#3B82F6" } }}
                    >
                      <PictureAsPdfOutlinedIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  </Tooltip>
                  {canEdit && (
                    <Tooltip title={inv.clientEmail ? "Email to client" : "No client email"}>
                      <span>
                        <IconButton
                          size="small"
                          disabled={busyRow === inv.id || !inv.clientEmail}
                          onClick={() => handleSendEmail(inv)}
                          sx={{ color: colors.textMuted, "&:hover": { background: colors.hover, color: "#34d399" } }}
                        >
                          <SendOutlinedIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                      </span>
                    </Tooltip>
                  )}
                  {canEdit && (
                    <Tooltip title="Edit">
                      <IconButton
                        size="small"
                        onClick={() => openEdit(inv.id)}
                        sx={{ color: colors.textMuted, "&:hover": { background: colors.hover, color: "#3B82F6" } }}
                      >
                        <EditOutlinedIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    </Tooltip>
                  )}
                  {canDelete && (
                    <Tooltip title="Delete">
                      <IconButton
                        size="small"
                        onClick={() => handleDelete(inv.id, inv.invoiceNo)}
                        sx={{ color: colors.textMuted, "&:hover": { background: "rgba(239,68,68,0.1)", color: "#ef4444" } }}
                      >
                        <DeleteOutlineIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    </Tooltip>
                  )}
                </Stack>
              </Box>
            );
          })
        )}
        </Box>

        {total > 0 && (
          <PaginationFooter
            page={page}
            pageSize={pageSize}
            total={total}
            onPageChange={setPage}
            onPageSizeChange={(n) => {
              setPageSize(n);
              setPage(1);
            }}
          />
        )}
      </FlatCard>

      {/* Add / Edit Invoice modal (spec §5) */}
      <AddInvoiceModal
        open={invoiceModalOpen}
        editId={editInvoiceId}
        onClose={closeInvoiceModal}
        onSuccess={handleInvoiceSaved}
      />
    </DashboardLayout>
  );
}

export default Invoices;
